import React, { useEffect, useState, useCallback, useRef } from "react";
import { Search, ChevronLeft, ChevronRight, Download, Users, LayoutDashboard, X, Loader2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatPrice } from "@/lib/helpers";
import FlyerReviewSettings from "@/components/FlyerReviewSettings";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT") : "—");
const gross = (v) => formatPrice(v || 0);

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const Pager = ({ page, pages, total, onPage }) => (
  <div data-testid="admin-pager" className="flex items-center justify-between mt-4 text-sm text-brand-navy/60">
    <span>{total} risultati · pagina {page} di {Math.max(1, pages)}</span>
    <div className="flex gap-2">
      <Button data-testid="pager-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded-full"><ChevronLeft className="h-4 w-4" /></Button>
      <Button data-testid="pager-next" variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)} className="rounded-full"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  </div>
);

const StatusBadge = ({ s }) => {
  const map = { paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-600", refunded: "bg-slate-200 text-slate-700" };
  const label = { paid: "Pagato", pending: "In attesa", failed: "Fallito", refunded: "Rimborsato" };
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${map[s] || "bg-slate-100 text-slate-600"}`}>{label[s] || s}</span>;
};

// Badge unificati Clienti e contatti (precedenza: Acquirente > Registrato > Contatto)
const CUST_BADGE = {
  acquirente: { label: "Acquirente", cls: "bg-emerald-100 text-emerald-700" },
  registrato: { label: "Registrato", cls: "bg-blue-100 text-blue-700" },
  contatto: { label: "Contatto", cls: "bg-slate-200 text-slate-600" },
};
const CustBadges = ({ badges, testid }) => (
  <span data-testid={testid} className="inline-flex flex-wrap gap-1">
    {(badges || []).map((b) => CUST_BADGE[b] ? (
      <span key={b} data-testid={`badge-${b}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${CUST_BADGE[b].cls}`}>{CUST_BADGE[b].label}</span>
    ) : null)}
  </span>
);

// ---------------------------------------------------------------------------
export const EnvBadge = () => {
  const [env, setEnv] = useState(null);
  useEffect(() => { api.get("/admin/environment").then(({ data }) => setEnv(data.environment)).catch(() => {}); }, []);
  if (!env) return null;
  const prod = env === "production";
  return (
    <span data-testid="admin-env-badge" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${prod ? "bg-emerald-500 text-white" : "bg-amber-400 text-brand-navy"}`}>
      {prod ? "Produzione" : "Preview"}
    </span>
  );
};

// ---------------------------------------------------------------------------
export const GlobalSearch = ({ onOpenCustomer, onOpenOrder }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const dq = useDebounced(q, 300);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!dq.trim()) { setRes(null); return; }
    setLoading(true);
    api.get("/admin/search", { params: { q: dq, limit: 6 } })
      .then(({ data }) => setRes(data)).catch(() => setRes(null)).finally(() => setLoading(false));
  }, [dq]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setRes(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const Group = ({ title, items, render }) => items?.length ? (
    <div className="py-2">
      <p className="px-3 text-[11px] font-bold uppercase text-brand-navy/40">{title}</p>
      {items.map(render)}
    </div>
  ) : null;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
        <Input data-testid="admin-global-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca clienti, ordini, email o prodotti…"
          className="pl-9 pr-9 rounded-full bg-white" />
        {q && <button data-testid="admin-search-clear" onClick={() => { setQ(""); setRes(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-navy/40"><X className="h-4 w-4" /></button>}
      </div>
      {(res || loading) && (
        <div data-testid="admin-search-results" className="absolute z-50 mt-2 w-full max-h-[70vh] overflow-auto rounded-2xl border border-brand-navy/10 bg-white shadow-xl divide-y divide-brand-navy/5">
          {loading && <div className="p-4 text-sm text-brand-navy/50 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</div>}
          {res && !loading && (
            <>
              <Group title="Clienti" items={res.customers} render={(c) => (
                <button key={c.id} data-testid={`search-customer-${c.id}`} onClick={() => { onOpenCustomer?.(c.id); setRes(null); }} className="w-full text-left px-3 py-2 hover:bg-brand-light">
                  <span className="text-sm font-medium text-brand-navy">{c.name}</span> <span className="text-xs text-brand-navy/50">{c.email}</span>
                </button>
              )} />
              <Group title="Ordini" items={res.orders} render={(o) => (
                <button key={o.id} data-testid={`search-order-${o.id}`} onClick={() => { onOpenOrder?.(o.id); setRes(null); }} className="w-full text-left px-3 py-2 hover:bg-brand-light flex justify-between">
                  <span className="text-sm text-brand-navy">#{o.id.slice(-6)} · {o.user_email}</span>
                  <span className="text-xs text-brand-navy/50">{gross(o.total)} · {o.stripe_masked}</span>
                </button>
              )} />
              <Group title="Messaggi / Email" items={res.messages} render={(m) => (
                <div key={m.id} className="px-3 py-2 hover:bg-brand-light">
                  <span className="text-[10px] uppercase text-brand-navy/40">{m.type}</span>
                  <span className="text-sm text-brand-navy ml-2">{m.name} · {m.email}</span>
                  <span className="block text-xs text-brand-navy/50">{m.subject}</span>
                </div>
              )} />
              <Group title="Prodotti" items={res.products} render={(p) => (
                <div key={p.id} className="px-3 py-2 hover:bg-brand-light flex justify-between">
                  <span className="text-sm text-brand-navy">{p.title_it}</span>
                  <span className="text-xs text-brand-navy/50">{p.category} · {gross(p.price)}</span>
                </div>
              )} />
              {!res.customers.length && !res.orders.length && !res.messages.length && !res.products.length &&
                <div className="p-4 text-sm text-brand-navy/50">Nessun risultato.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Stat = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className="rounded-2xl border border-brand-navy/10 bg-white p-4">
    <p className="text-xs text-brand-navy/50">{label}</p>
    <p className={`mt-1 text-2xl font-extrabold ${accent || "text-brand-navy"}`}>{value}</p>
  </div>
);

export const UnlockHistoricalOrder = () => {
  const [state, setState] = useState("idle"); // idle|checked|done|error
  const [dry, setDry] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const c = dry?.candidate;
  const confirmOk = confirm.trim().replace(/^#/, "") === "74d206";
  const eligible = !!(dry && dry.ok && dry.candidate_count === 1 && dry.eligible &&
    !dry.already_executed && c && c.short_id === "74d206" && c.payment_status === "paid");

  const runDry = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/orders/unlock-74d206/dry-run");
      setDry(data);
      setState(data.already_executed ? "done" : "checked");
      if (data.already_executed) setDone({ already: true });
    } catch { setState("error"); setErr("Verifica non riuscita (o non autorizzato)."); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/orders/unlock-74d206/apply", { confirm });
      setDone(data); setState("done");
    } catch (e) {
      setState("checked");
      setErr(e?.response?.data?.detail || "Sblocco non riuscito o non consentito.");
    } finally { setBusy(false); }
  };

  const statusLabel = { idle: "Da verificare", checked: "Verificato", done: "Completato", error: "Errore" }[state];
  const statusColor = { idle: "bg-slate-100 text-slate-600", checked: "bg-blue-100 text-blue-700", done: "bg-emerald-100 text-emerald-700", error: "bg-red-100 text-red-600" }[state];

  return (
    <div data-testid="unlock-74d206-box" className="rounded-2xl border border-amber-300 bg-amber-50/40 p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-brand-navy">Sblocco ordine storico #74d206 <span className="text-[10px] font-normal text-brand-navy/40">(temporaneo)</span></h3>
        <span data-testid="unlock-74d206-status" className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor}`}>{statusLabel}</span>
      </div>
      <p className="mt-1 text-xs text-brand-navy/50">Modifica esclusivamente <b>unlocked_at</b>. Non tocca pagamento, importo, Stripe, prodotto o file. Applicazione unica con guardia persistente.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button data-testid="unlock-74d206-verify" onClick={runDry} disabled={busy || state === "done"} variant="outline" className="rounded-full">Esegui verifica</Button>
      </div>

      {dry && state !== "done" && (
        <div data-testid="unlock-74d206-dryrun" className="mt-3 text-sm space-y-1 rounded-xl bg-white border border-brand-navy/10 p-3">
          <p>Candidati trovati: <b>{dry.candidate_count}</b></p>
          {c ? (<>
            <p>Ordine: <b>#{c.short_id}</b> <span className="text-brand-navy/40 font-mono text-xs">({c.order_id})</span></p>
            <p>Prodotto: {c.title || "—"}</p>
            <p>Importo: <b>{gross(c.total)}</b> · Stato pagamento: <b>{c.payment_status}</b></p>
            <p>Sessione Stripe: <span className="font-mono text-xs">{c.stripe_masked}</span> · Live: <b>{c.session_live?.exists ? `sì (${c.session_live.payment_status || c.session_live.status || "?"})` : "non recuperabile"}</b></p>
            <p>File disponibile: <b className={c.file_available ? "text-emerald-600" : "text-red-600"}>{c.file_available ? "sì" : "no"}</b></p>
            <p>unlocked_at attuale: <b>{c.unlocked_at ? fmtDate(c.unlocked_at) : "non sbloccato"}</b></p>
          </>) : (
            <p className="text-brand-navy/60">Nessun ordine #74d206 in questo ambiente (in preview è normale: l'ordine esiste solo in produzione).</p>
          )}
          {!eligible && <p className="text-red-600 text-xs mt-1">Condizioni non soddisfatte: sblocco non disponibile in questo ambiente.</p>}
        </div>
      )}

      {state === "checked" && eligible && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-brand-navy/60">Per confermare digita <b>#74d206</b>:</span>
          <Input data-testid="unlock-74d206-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="#74d206" className="w-40 rounded-full" />
          <Button data-testid="unlock-74d206-apply" onClick={apply} disabled={busy || !confirmOk} className="rounded-full bg-brand-electric text-white disabled:opacity-50">Sblocca ordine</Button>
        </div>
      )}

      {state === "done" && (
        <div data-testid="unlock-74d206-done" className="mt-3 text-sm rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-emerald-800">
          <b>Completato.</b> {done?.already ? "Lo sblocco era già stato eseguito." : <>Ordine #74d206 sbloccato (unlocked_at impostato{done?.unlocked_at ? ` il ${fmtDate(done.unlocked_at)}` : ""}).</>} Ulteriori esecuzioni sono impedite dalla guardia persistente.
        </div>
      )}
      {err && <p data-testid="unlock-74d206-error" className="mt-2 text-sm text-red-600">{err}</p>}
    </div>
  );
};

export const AdminOverview = ({ onOpenOrder }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <p className="text-brand-navy/50 py-10 text-center">Caricamento…</p>;
  const sys = d.system;
  return (
    <div data-testid="admin-overview" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Stat testid="ov-gross-today" label="Incasso lordo oggi" value={gross(d.revenue.gross_today)} accent="text-emerald-600" />
        <Stat testid="ov-gross-month" label="Incasso lordo mese" value={gross(d.revenue.gross_month)} accent="text-emerald-600" />
        <Stat testid="ov-gross-total" label="Incasso lordo totale" value={gross(d.revenue.gross_total)} accent="text-emerald-600" />
        <Stat testid="ov-orders-paid" label="Ordini pagati" value={d.orders.paid} />
        <Stat testid="ov-orders-pending" label="Ordini in attesa" value={d.orders.pending} />
        <Stat testid="ov-orders-refunded" label="Ordini rimborsati" value={d.orders.refunded} />
        <Stat testid="ov-new-customers" label="Nuovi clienti (oggi)" value={d.customers.today} />
        <Stat testid="ov-new-messages" label="Nuovi messaggi (oggi)" value={d.messages.new_today} />
        <Stat testid="ov-new-emails" label="Nuove email (oggi)" value={d.emails.new_today} />
        <Stat testid="ov-review-emails" label="Email da rivedere" value={d.emails.need_human_review} accent="text-amber-600" />
        <Stat testid="ov-products" label="Prodotti pubblicati" value={d.products_published} />
      </div>
      <p className="text-xs text-brand-navy/40">{d.revenue.net_note}</p>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-brand-navy/10 bg-white p-4">
          <h3 className="font-heading font-bold text-brand-navy mb-3">Stato sistema</h3>
          <ul className="text-sm space-y-1.5">
            <li>Backend: <b className="text-emerald-600">{sys.backend}</b></li>
            <li>Database: <b className={sys.database === "ok" ? "text-emerald-600" : "text-red-600"}>{sys.database}</b></li>
            <li>IMAP: <b className={sys.imap_configured ? "text-emerald-600" : "text-red-600"}>{sys.imap_configured ? "configurato" : "non configurato"}</b> · ultima sync: {sys.imap_last_sync_source} ({fmtDate(sys.imap_last_sync_at)})</li>
            <li>Ultimo evento pagamento: {sys.last_payment_event ? <><b>{sys.last_payment_event.payment_status}</b> · {fmtDate(sys.last_payment_event.at)}</> : "—"}</li>
          </ul>
          {sys.last_payment_event?.note && <p className="mt-2 text-[11px] text-brand-navy/40">{sys.last_payment_event.note}</p>}
        </div>
        <div className="rounded-2xl border border-brand-navy/10 bg-white p-4">
          <h3 className="font-heading font-bold text-brand-navy mb-3">Ultimi ordini pagati</h3>
          <ul className="text-sm divide-y divide-brand-navy/5">
            {d.recent_paid_orders.map((o) => (
              <li key={o.id}><button data-testid={`ov-order-${o.id}`} onClick={() => onOpenOrder?.(o.id)} className="w-full text-left py-1.5 flex justify-between hover:text-brand-electric">
                <span>#{o.id.slice(-6)} · {o.user_email}</span><span className="font-semibold">{gross(o.total)}</span></button></li>
            ))}
            {!d.recent_paid_orders.length && <li className="py-2 text-brand-navy/40">—</li>}
          </ul>
        </div>
      </div>
      <div className="rounded-2xl border border-brand-navy/10 bg-white p-4">
        <h3 className="font-heading font-bold text-brand-navy mb-3">Ultime richieste ricevute</h3>
        <ul className="text-sm divide-y divide-brand-navy/5">
          {d.recent_requests.map((m) => (
            <li key={m.id} className="py-1.5 flex justify-between"><span>{m.name} · {m.email}</span><span className="text-brand-navy/50">{m.subject} · {fmtDate(m.created_at)}</span></li>
          ))}
          {!d.recent_requests.length && <li className="py-2 text-brand-navy/40">—</li>}
        </ul>
      </div>
      <UnlockHistoricalOrder />
    </div>
  );
};

// ---------------------------------------------------------------------------
export const AdminCustomers = ({ openId, onConsumeOpen }) => {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("real");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [detail, setDetail] = useState(null);
  const dq = useDebounced(q, 300);
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const goPage = (p) => { setPage(p); requestAnimationFrame(() => listRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };
  useEffect(() => { if (detail && detailRef.current) detailRef.current.scrollTop = 0; }, [detail]);

  const load = useCallback(() => {
    api.get("/admin/customers", { params: { q: dq, scope, page, page_size: 20 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [dq, scope, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, scope]);

  const openDetail = useCallback((key) => {
    const k = String(key || "");
    const req = k.includes("@")
      ? api.get("/admin/customers/lookup", { params: { email: k } })
      : api.get(`/admin/customers/${k}`);
    req.then(({ data }) => setDetail(data)).catch(() => {});
  }, []);
  useEffect(() => { if (openId) { openDetail(openId); onConsumeOpen?.(); } }, [openId, openDetail, onConsumeOpen]);

  return (
    <div ref={listRef} data-testid="admin-customers" className="scroll-mt-24">
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
          <Input data-testid="customers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o email…" className="pl-9 rounded-full" />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="customers-scope" className="w-40 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="real">Reali</SelectItem><SelectItem value="test">Test/DEMO</SelectItem><SelectItem value="all">Tutti</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="overflow-auto rounded-2xl border border-brand-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
            <th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Tipo</th>
            <th className="p-3">Ordini</th><th className="p-3">Speso</th><th className="p-3">Ultimo ordine</th><th className="p-3">Msg</th>
          </tr></thead>
          <tbody className="divide-y divide-brand-navy/5">
            {data.items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => openDetail(c.id)} className="hover:bg-brand-light cursor-pointer">
                <td className="p-3 font-medium text-brand-navy">{c.name}{c.is_test && <span className="ml-2 text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">TEST</span>}</td>
                <td className="p-3 text-brand-navy/70">{c.email}</td>
                <td className="p-3"><CustBadges badges={c.badges} testid={`customer-badges-${c.id}`} /></td>
                <td className="p-3">{c.paid_orders}</td><td className="p-3 font-semibold">{gross(c.total_spent)}</td>
                <td className="p-3 text-brand-navy/50">{fmtDate(c.last_order_at)}</td><td className="p-3">{c.messages_count}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={7} className="p-6 text-center text-brand-navy/40">Nessun cliente o contatto.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={goPage} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent ref={detailRef} className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="customer-detail">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">{detail.name} <CustBadges badges={detail.badges} testid="customer-detail-badges" />{detail.is_test && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">TEST</span>}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Email</span>{detail.email}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Totale speso</span>{gross(detail.total_spent)}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Account</span>{detail.is_account ? `Registrato (${detail.provider || "password"})` : "Solo contatto (nessun account)"}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Ultima attività</span>{fmtDate(detail.last_activity)}</div>
              </div>
              <div><p className="font-semibold text-brand-navy mb-1">Storico ordini ({detail.orders.length})</p>
                <ul className="divide-y divide-brand-navy/5 max-h-40 overflow-auto">
                  {detail.orders.map((o) => <li key={o.id} className="py-1.5 flex justify-between"><span>#{o.id.slice(-6)} · {o.items.join(", ")}</span><span className="flex items-center gap-2"><StatusBadge s={o.payment_status} />{gross(o.total)}</span></li>)}
                  {!detail.orders.length && <li className="py-2 text-brand-navy/40">—</li>}
                </ul>
              </div>
              <div><p className="font-semibold text-brand-navy mb-1">Prodotti acquistati</p>
                <p className="text-brand-navy/70">{detail.products_bought.map((p) => `${p.title} x${p.quantity}`).join(", ") || "—"}</p></div>
              <div><p className="font-semibold text-brand-navy mb-1">Richieste Contatti ({detail.messages.length}) · Email collegate ({detail.emails.length})</p>
                <ul className="text-brand-navy/70 max-h-32 overflow-auto">
                  {detail.messages.map((m) => <li key={m.id}>· {m.subject} <span className="text-brand-navy/40">{fmtDate(m.created_at)}</span></li>)}
                </ul>
              </div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
export const AdminOrders = ({ openId, onConsumeOpen }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [scope, setScope] = useState("real");
  const [period, setPeriod] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [detail, setDetail] = useState(null);
  const dq = useDebounced(q, 300);
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const goPage = (p) => { setPage(p); requestAnimationFrame(() => listRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };
  useEffect(() => { if (detail && detailRef.current) detailRef.current.scrollTop = 0; }, [detail]);

  const load = useCallback(() => {
    api.get("/admin/orders/list", { params: { q: dq, status, scope, period, page, page_size: 20 } }).then(({ data }) => setData(data)).catch(() => {});
  }, [dq, status, scope, period, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, status, scope, period]);

  const openDetail = useCallback((id) => api.get(`/admin/orders/detail/${id}`).then(({ data }) => setDetail(data)).catch(() => {}), []);
  useEffect(() => { if (openId) { openDetail(openId); onConsumeOpen?.(); } }, [openId, openDetail, onConsumeOpen]);

  const setClassification = async (id, value) => {
    await api.patch(`/admin/orders/detail/${id}/classification`, { classification: value });
    await openDetail(id); load();
  };

  const exportCsv = async () => {
    const { data } = await api.get("/admin/orders/export", { params: { q: dq, status, scope, period }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "ordini.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div ref={listRef} data-testid="admin-orders-office" className="scroll-mt-24">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
          <Input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ID (# opzionale), nome, email, prodotto…" className="pl-9 rounded-full" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="orders-status" className="w-36 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="paid">Pagato</SelectItem><SelectItem value="pending">In attesa</SelectItem><SelectItem value="failed">Fallito</SelectItem><SelectItem value="refunded">Rimborsato</SelectItem></SelectContent></Select>
        <Select value={scope} onValueChange={setScope}><SelectTrigger data-testid="orders-scope" className="w-32 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="real">Reali</SelectItem><SelectItem value="test">Test/DEMO</SelectItem><SelectItem value="all">Tutti</SelectItem></SelectContent></Select>
        <Select value={period} onValueChange={setPeriod}><SelectTrigger data-testid="orders-period" className="w-32 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Sempre</SelectItem><SelectItem value="today">Oggi</SelectItem><SelectItem value="month">Questo mese</SelectItem><SelectItem value="7d">7 giorni</SelectItem><SelectItem value="30d">30 giorni</SelectItem></SelectContent></Select>
        <Button data-testid="orders-export" onClick={exportCsv} variant="outline" className="rounded-full gap-2"><Download className="h-4 w-4" /> CSV</Button>
      </div>
      <div className="overflow-auto rounded-2xl border border-brand-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
            <th className="p-3">Ordine</th><th className="p-3">Data</th><th className="p-3">Cliente</th><th className="p-3">Prodotti</th>
            <th className="p-3">Totale</th><th className="p-3">Stato</th><th className="p-3">Consegna</th><th className="p-3">Stripe</th>
          </tr></thead>
          <tbody className="divide-y divide-brand-navy/5">
            {data.items.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => openDetail(o.id)} className="hover:bg-brand-light cursor-pointer">
                <td className="p-3 font-mono text-xs">#{o.id.slice(-6)}{o.is_test && <span className="ml-1 text-[9px] bg-slate-200 text-slate-600 px-1 rounded">TEST</span>}</td>
                <td className="p-3 text-brand-navy/50">{fmtDate(o.created_at)}</td>
                <td className="p-3">{o.user_name || o.user_email}</td>
                <td className="p-3 text-brand-navy/60">{o.items.map((i) => `${i.title} x${i.quantity}`).join(", ")}</td>
                <td className="p-3 font-semibold">{gross(o.total)}</td>
                <td className="p-3"><StatusBadge s={o.payment_status} /></td>
                <td className="p-3 text-xs text-brand-navy/50">{o.download_status}</td>
                <td className="p-3 font-mono text-xs text-brand-navy/40">{o.stripe_masked}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={8} className="p-6 text-center text-brand-navy/40">Nessun ordine.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={goPage} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent ref={detailRef} className="max-w-lg max-h-[85vh] overflow-auto" data-testid="order-detail">
          {detail && (<>
            <DialogHeader><DialogTitle>Ordine #{detail.id.slice(-6)} {detail.is_test && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">TEST</span>}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Data</span>{fmtDate(detail.created_at)}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Cliente</span>{detail.user_name || detail.user_email}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Importo</span>{gross(detail.total)}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Stato</span><StatusBadge s={detail.payment_status} /></div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Consegna</span>{detail.download_status}</div>
                <div className="rounded-lg bg-brand-navy/5 px-3 py-2"><span className="text-xs text-brand-navy/50 block">Stripe (mascherato)</span><span className="font-mono text-xs">{detail.stripe_masked}</span></div>
              </div>
              <div><p className="font-semibold text-brand-navy mb-1">Prodotti</p>
                <ul>{detail.items.map((i, k) => <li key={k}>· {i.title} x{i.quantity} — {gross(i.price)}</li>)}</ul></div>
              <div className="rounded-lg border border-brand-navy/10 p-3">
                <p className="text-xs text-brand-navy/50 mb-1.5">Classificazione amministrativa (non modifica pagamento, stato o contenuto)</p>
                <div className="flex items-center gap-2">
                  <Select value={detail.admin_classification || "unclassified"} onValueChange={(v) => setClassification(detail.id, v)}>
                    <SelectTrigger data-testid="order-classification" className="w-52 rounded-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="real">Reale</SelectItem>
                      <SelectItem value="test_demo">Test/DEMO</SelectItem>
                      <SelectItem value="unclassified">Non classificato (euristica)</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${detail.is_test ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>{detail.is_test ? "conteggiato come Test" : "conteggiato come Reale"}</span>
                </div>
              </div>
              {detail.linked_messages?.length > 0 && <div><p className="font-semibold text-brand-navy mb-1">Messaggi collegati</p>
                <ul className="text-brand-navy/70">{detail.linked_messages.map((m) => <li key={m.id}>· {m.subject} ({fmtDate(m.created_at)})</li>)}</ul></div>}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
export const AdminMessages = () => {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, new_count: 0 });
  const dq = useDebounced(q, 300);
  const listRef = useRef(null);
  const goPage = (p) => { setPage(p); requestAnimationFrame(() => listRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };

  const load = useCallback(() => {
    api.get("/admin/messages/list", { params: { q: dq, scope, status, page, page_size: 20 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [dq, scope, status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, scope, status]);

  const changeStatus = async (id, value) => {
    await api.patch(`/admin/messages/${id}/status`, { status: value });
    load();
  };

  return (
    <div ref={listRef} data-testid="admin-messages-office" className="scroll-mt-24">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <span data-testid="messages-new-count" className="text-sm font-semibold text-brand-navy bg-amber-100 px-3 py-1 rounded-full">{data.new_count} nuovi</span>
        {data.unclassified_count > 0 && <span data-testid="messages-unclassified-count" className="text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">{data.unclassified_count} da classificare</span>}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
          <Input data-testid="messages-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email o contenuto…" className="pl-9 rounded-full" />
        </div>
        <Select value={scope} onValueChange={setScope}><SelectTrigger data-testid="messages-scope" className="w-32 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="real">Reali</SelectItem><SelectItem value="test">Test/DEMO</SelectItem></SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="messages-status" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="da_classificare">Da classificare</SelectItem><SelectItem value="nuovo">Nuovo</SelectItem><SelectItem value="in_lavorazione">In lavorazione</SelectItem><SelectItem value="risolto">Risolto</SelectItem><SelectItem value="archiviato">Archiviato</SelectItem></SelectContent></Select>
      </div>
      <div className="space-y-2">
        {data.items.map((m) => (
          <div key={m.id} data-testid={`message-row-${m.id}`} className="rounded-xl border border-brand-navy/10 bg-white p-4">
            <div className="flex justify-between items-start gap-3">
              <div>
                <p className="font-semibold text-brand-navy">{m.name} <span className="text-brand-navy/40 font-normal text-sm">· {m.email}</span>
                  {m.is_test && <span className="ml-2 text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">TEST</span>}
                  {m.customer_id && <span className="ml-2 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">cliente</span>}
                </p>
                <p className="text-xs text-brand-navy/40">{m.subject} · {fmtDate(m.created_at)} · {m.source}</p>
                <p className="mt-1 text-sm text-brand-navy/70 line-clamp-2">{m.message}</p>
              </div>
              <Select value={["nuovo","in_lavorazione","risolto","archiviato"].includes(m.status) ? m.status : ""} onValueChange={(v) => changeStatus(m.id, v)}>
                <SelectTrigger data-testid={`message-status-${m.id}`} className="w-40 rounded-full h-8 text-xs"><SelectValue placeholder="Da classificare" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nuovo">Nuovo</SelectItem><SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                  <SelectItem value="risolto">Risolto</SelectItem><SelectItem value="archiviato">Archiviato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {!data.items.length && <p className="text-brand-navy/40 py-8 text-center">Nessun messaggio.</p>}
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={goPage} />
    </div>
  );
};

// ---------------------------------------------------------------------------
export const AdminProducts = ({ onNew, onEdit, onDelete, reloadKey }) => {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, categories: [] });
  const dq = useDebounced(q, 300);
  const listRef = useRef(null);
  const goPage = (p) => { setPage(p); requestAnimationFrame(() => listRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };

  const load = useCallback(() => {
    api.get("/admin/products/list", { params: { q: dq, category, status, page, page_size: 12, sort: "order", order: "asc" } })
      .then(({ data }) => setData(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq, category, status, page, reloadKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, category, status]);

  const move = async (id, direction) => {
    setData((d) => {
      const items = [...d.items];
      const idx = items.findIndex((p) => p.id === id);
      const swap = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || swap < 0 || swap >= items.length) return d;
      [items[idx], items[swap]] = [items[swap], items[idx]];
      return { ...d, items };
    });
    try { await api.post(`/admin/products/${id}/move`, { direction }); load(); }
    catch { toast.error("Spostamento non riuscito"); load(); }
  };

  const duplicate = async (id) => {
    try { await api.post(`/admin/products/${id}/duplicate`); toast.success("Prodotto duplicato (nascosto)"); load(); }
    catch { toast.error("Duplicazione non riuscita"); }
  };

  const downloadFile = async (id) => {
    try {
      const res = await api.get(`/admin/products/${id}/file`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const fname = m ? decodeURIComponent(m[1]) : "file";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("File non disponibile per questo prodotto.");
    }
  };

  return (
    <div ref={listRef} data-testid="admin-products-office" className="scroll-mt-24">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
          <Input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per titolo o SKU…" className="pl-9 rounded-full" />
        </div>
        <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="products-category" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{data.categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name_it}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="products-status" className="w-40 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="published">Pubblicato</SelectItem><SelectItem value="hidden">Nascosto</SelectItem></SelectContent></Select>
        {onNew && <Button data-testid="products-new" onClick={onNew} className="rounded-full bg-brand-electric text-white">Nuovo prodotto</Button>}
      </div>
      <div className="overflow-auto rounded-2xl border border-brand-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
            <th className="p-3 w-12">Ord.</th>
            <th className="p-3">Titolo</th><th className="p-3">Tipo</th><th className="p-3">Categoria</th><th className="p-3">Prezzo</th>
            <th className="p-3">Stato</th><th className="p-3">File</th><th className="p-3">Vendite</th><th className="p-3">Incasso lordo</th><th className="p-3">Azioni</th>
          </tr></thead>
          <tbody className="divide-y divide-brand-navy/5">
            {data.items.map((p, i) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`} className="hover:bg-brand-light">
                <td className="p-3">
                  <div className="flex flex-col gap-0.5">
                    <button data-testid={`product-up-${p.id}`} disabled={i === 0} onClick={() => move(p.id, "up")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowUp className="h-3 w-3" /></button>
                    <button data-testid={`product-down-${p.id}`} disabled={i === data.items.length - 1} onClick={() => move(p.id, "down")} className="h-6 w-6 rounded border border-brand-navy/15 flex items-center justify-center text-brand-navy/60 disabled:opacity-30 hover:border-brand-electric"><ArrowDown className="h-3 w-3" /></button>
                  </div>
                </td>
                <td className="p-3 font-medium text-brand-navy">{p.title_it}{p.featured && <span className="ml-2 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">EVIDENZA</span>}{p.is_demo && <span className="ml-2 text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">DEMO</span>}</td>
                <td className="p-3">{p.product_type === "amazon" ? <span className="text-[10px] font-semibold text-[#b36b00] bg-[#FF9900]/15 px-1.5 py-0.5 rounded-full">Amazon</span> : <span className="text-[10px] text-brand-navy/50">Digitale</span>}</td>
                <td className="p-3 text-brand-navy/60">{p.category}</td>
                <td className="p-3">{gross(p.price)}</td>
                <td className="p-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.status === "pubblicato" ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>{p.status}</span></td>
                <td className="p-3" data-testid={`product-file-${p.id}`}>
                  {p.has_file
                    ? <span className="inline-flex flex-col"><span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-full w-fit">File presente</span>{p.file_name && <span className="mt-0.5 text-[10px] text-brand-navy/40 max-w-[160px] truncate" title={p.file_name}>{p.file_name}</span>}</span>
                    : <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">Nessun file</span>}
                </td>
                <td className="p-3">{p.sales_paid}</td>
                <td className="p-3 font-semibold">{gross(p.gross_revenue)}</td>
                <td className="p-3">
                  <div className="flex gap-2 items-center">
                    <a data-testid={`product-preview-${p.id}`} href={`/prodotti/${p.id}`} target="_blank" rel="noreferrer" className="text-brand-electric text-xs underline">Anteprima</a>
                    {p.has_file && <button data-testid={`product-download-${p.id}`} onClick={() => downloadFile(p.id)} className="inline-flex items-center gap-1 text-emerald-700 text-xs underline"><Download className="h-3 w-3" /> Scarica file</button>}
                    {onEdit && <button onClick={() => onEdit(p.id)} className="text-brand-navy/60 text-xs underline">Modifica</button>}
                    <button data-testid={`product-duplicate-${p.id}`} onClick={() => duplicate(p.id)} className="text-brand-navy/60 text-xs underline">Duplica</button>
                    {onDelete && <button onClick={() => onDelete(p.id)} className="text-red-500 text-xs underline">Elimina</button>}
                  </div>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={10} className="p-6 text-center text-brand-navy/40">Nessun prodotto.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={goPage} />
    </div>
  );
};

// ---------------------------------------------------------------------------
const MON_STATE = {
  queued: { txt: "In coda", cls: "bg-blue-100 text-blue-700" },
  working: { txt: "In preparazione", cls: "bg-blue-100 text-blue-700" },
  qc: { txt: "Controllo qualità", cls: "bg-indigo-100 text-indigo-700" },
  done: { txt: "Completato", cls: "bg-emerald-100 text-emerald-700" },
  delivered: { txt: "Consegnato", cls: "bg-emerald-100 text-emerald-700" },
  needs_review: { txt: "Da verificare", cls: "bg-amber-100 text-amber-700" },
  failed: { txt: "Errore", cls: "bg-red-100 text-red-700" },
  interrupted: { txt: "Interrotto", cls: "bg-red-100 text-red-700" },
  idle: { txt: "Inattivo", cls: "bg-brand-navy/5 text-brand-navy/50" },
};

export const FlyerMonitor = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/flyer/monitor", { params: { limit: 40 } });
      setRows(data.projects || []);
      setUpdatedAt(new Date());
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Monitor non disponibile.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    if (auto) {
      timer.current = setInterval(load, 8000);
      return () => clearInterval(timer.current);
    }
  }, [auto, load]);

  const activeCount = rows.filter((r) => ["queued", "working", "qc"].includes(r.state)).length;
  const errorCount = rows.filter((r) => ["failed", "interrupted"].includes(r.state)).length;
  const totalCost = rows.reduce((s, r) => s + (r.ai_cost_eur || 0), 0);
  const st = (s) => MON_STATE[s] || { txt: s || "—", cls: "bg-brand-navy/5 text-brand-navy/50" };

  return (
    <div data-testid="flyer-monitor" className="rounded-2xl border border-brand-navy/10 bg-white p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-brand-navy flex items-center gap-2"><LayoutDashboard className="h-4 w-4 text-brand-electric" /> Monitor generazioni</h3>
          <p className="text-xs text-brand-navy/50">Stato in tempo reale dei progetti flyer: fase, errori, chiamate AI, costo e QC.</p>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="flyer-monitor-active" className="text-xs font-semibold bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">In corso: {activeCount}</span>
          <span data-testid="flyer-monitor-errors" className="text-xs font-semibold bg-red-50 text-red-700 px-2.5 py-1 rounded-full">Errori: {errorCount}</span>
          <span data-testid="flyer-monitor-cost" className="text-xs font-semibold bg-brand-light text-brand-navy px-2.5 py-1 rounded-full">Costo AI: {gross(totalCost)}</span>
          <button data-testid="flyer-monitor-auto" onClick={() => setAuto((a) => !a)} className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${auto ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-brand-navy/60 border-brand-navy/15"}`}>Auto {auto ? "ON" : "OFF"}</button>
          <button data-testid="flyer-monitor-refresh" onClick={load} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-electric text-white flex items-center gap-1">{loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Aggiorna</button>
        </div>
      </div>
      {err && <div data-testid="flyer-monitor-error" className="text-sm text-red-600 mb-2">{err}</div>}
      <div className="overflow-auto rounded-xl border border-brand-navy/10">
        <table className="w-full text-xs">
          <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
            <th className="p-2">Codice</th><th className="p-2">Attività</th><th className="p-2">Stato</th><th className="p-2">Fase</th>
            <th className="p-2">Errore</th><th className="p-2">AI</th><th className="p-2">Costo</th><th className="p-2">QC</th><th className="p-2">Rec.</th><th className="p-2">Origine</th><th className="p-2">Aggiornato</th>
          </tr></thead>
          <tbody className="divide-y divide-brand-navy/5">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`flyer-monitor-row-${r.code || r.id}`} className="hover:bg-brand-light">
                <td className="p-2 font-mono">{r.code || "—"}</td>
                <td className="p-2">{r.business || "—"}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded-full font-semibold ${st(r.state).cls}`}>{st(r.state).txt}</span></td>
                <td className="p-2 text-brand-navy/60 min-w-[150px]">
                  {["queued", "working", "qc"].includes(r.state) && r.progress_percent != null ? (
                    <div data-testid={`monitor-progress-${r.code || r.id}`}>
                      <div className="flex justify-between items-center mb-0.5"><span className="text-[10px] text-brand-navy/60 truncate max-w-[110px]">{r.progress_message || r.stage}</span><span className="text-[10px] font-mono text-brand-navy/40">{r.progress_percent}%</span></div>
                      <div className="h-1.5 rounded-full bg-brand-navy/10 overflow-hidden"><div className="h-full bg-brand-electric rounded-full transition-all duration-500" style={{ width: `${r.progress_percent}%` }} /></div>
                    </div>
                  ) : (r.stage || "—")}
                </td>
                <td className={`p-2 max-w-[180px] truncate ${r.error_type ? "text-red-600" : "text-brand-navy/30"}`} title={r.error_detail || ""}>{r.error_type ? `${r.error_type}${r.error_detail ? " — " + r.error_detail : ""}` : "—"}</td>
                <td className="p-2 text-center">{r.ai_calls ?? 0}</td>
                <td className="p-2">{gross(r.ai_cost_eur || 0)}</td>
                <td className="p-2">{r.qc != null ? `${r.qc}/100` : "—"}</td>
                <td className="p-2 text-center">{r.recovery_count || 0}</td>
                <td className="p-2 text-brand-navy/50">{r.generation_source === "admin_on_behalf" ? "Admin" : (r.payment_status === "paid" || r.delivered ? "Cliente" : "—")}</td>
                <td className="p-2 text-brand-navy/40">{fmtDate(r.updated_at)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={11} className="p-6 text-center text-brand-navy/40">Nessun progetto da monitorare.</td></tr>}
          </tbody>
        </table>
      </div>
      {updatedAt && <p className="text-[11px] text-brand-navy/40 mt-2">Ultimo aggiornamento: {updatedAt.toLocaleTimeString("it-IT")}</p>}
    </div>
  );
};


const FLY_ASSET = (process.env.REACT_APP_BACKEND_URL || "");
export const AdminFlyerProjects = () => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, counts: {}, total_all: 0 });
  const [detail, setDetail] = useState(null);
  const [editingReview, setEditingReview] = useState(false);
  const [reviewDraft, setReviewDraft] = useState({ business: {}, content: {}, objective_type: "" });
  const [reviewSaving, setReviewSaving] = useState(false);
  const [f5b, setF5b] = useState(null);
  const [f5bBusy, setF5bBusy] = useState("");
  const [deliv, setDeliv] = useState(null);
  const correctionKeyRef = useRef(null);
  const dq = useDebounced(q, 300);
  const listRef = useRef(null);
  const detailRef = useRef(null);
  const goPage = (p) => { setPage(p); requestAnimationFrame(() => listRef.current?.scrollIntoView({ block: "start", behavior: "auto" })); };
  useEffect(() => { if (detail && detailRef.current) detailRef.current.scrollTop = 0; }, [detail]);

  const load = useCallback(() => {
    api.get("/admin/flyer/projects", { params: { q: dq, status, page, page_size: 20 } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [dq, status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, status]);

  const open = (id) => api.get(`/admin/flyer/projects/${id}`).then(({ data }) => setDetail(data)).catch(() => {});
  const STATUSES = ["draft", "submitted", "in_work", "preview_ready", "needs_review", "revision_requested", "approved", "approved_pending_delivery", "delivered", "cancelled"];
  const STATUS_LABEL = { draft: "Bozza", submitted: "Inviato", in_work: "In lavorazione", preview_ready: "Anteprima pronta", needs_review: "Da verificare", revision_requested: "Revisione richiesta", approved: "Approvato", approved_pending_delivery: "Approvato · in attesa di consegna", delivered: "Consegnato", cancelled: "Annullato" };
  const SL = (s) => STATUS_LABEL[s] || s;
  const FLY_FMT = { facebook: "Facebook", instagram: "Instagram", reel: "Reel/Story" };
  const dlAdmin = (path) => { const a = document.createElement("a"); a.href = FLY_ASSET + path; a.target = "_blank"; a.click(); };
  const genNow = async () => { await api.post(`/admin/flyer/projects/${detail.id}/generate`); await open(detail.id); load(); };
  const recoverGen = async () => {
    const MSG = {
      already_completed: "Il progetto è già stato completato.",
      recovery_limit_reached: "Limite di recuperi raggiunto (massimo 2).",
      has_ai_usage: "Il progetto ha già consumato generazioni. Usa \"Correggi i dati\" se il brief contiene un errore.",
      state_not_recoverable: "Il progetto non è in uno stato recuperabile.",
      not_stuck_yet: "Generazione ancora in corso: riprova tra qualche minuto.",
      already_recovering: "Un recupero è già in corso.",
      not_recoverable: "Nessun job recuperabile.",
    };
    try {
      const { data } = await api.post(`/admin/flyer/projects/${detail.id}/recover-generation`);
      if (!data.started) alert(MSG[data.reason] || "Recupero non disponibile.");
      await open(detail.id); load();
    } catch (e) { alert(e?.response?.data?.detail || "Recupero non disponibile."); }
  };
  const isStuck = (d) => {
    if (!d || d.delivered || d.generated_at) return false;
    const cs = d.creative_status || {};
    if (!["queued", "working", "qc"].includes(cs.state)) return false;
    if (!cs.updated_at) return false;
    return (Date.now() - new Date(cs.updated_at).getTime()) > 10 * 60 * 1000;
  };
  const canRetry = (d) => {
    if (!d || d.delivered || d.generated_at) return false;
    const cs = d.creative_status || {};
    return ["failed", "interrupted"].includes(cs.state) && (d.recovery_count || 0) < 2;
  };
  const techLabel = (d) => {
    const cs = (d && d.creative_status) || {};
    if (["queued", "working", "qc"].includes(cs.state)) return { txt: `Generazione in corso${cs.stage ? " — " + cs.stage : ""}`, cls: "text-brand-electric" };
    if (["failed", "interrupted"].includes(cs.state)) return { txt: `Errore tecnico${cs.error_type ? " (" + cs.error_type + ")" : ""}${cs.stage ? " — fase: " + cs.stage : ""}`, cls: "text-red-600" };
    return null;
  };
  const deliverNow = async () => {
    try { await api.post(`/admin/flyer/projects/${detail.id}/deliver`); await open(detail.id); load(); }
    catch (e) { alert(e?.response?.data?.detail || "Consegna non disponibile."); }
  };
  const reBrand = async () => {
    try { await api.post(`/admin/flyer/projects/${detail.id}/brand-research`); await open(detail.id); }
    catch (e) { alert(e?.response?.data?.detail || "Analisi non disponibile."); }
  };
  const inHumanReview = (d) => {
    const state = (d?.creative_status || {}).state;
    return d && !d.delivered && (d.status === "needs_review" || ["needs_review", "failed", "interrupted"].includes(state));
  };
  const hasReviewOutputs = (d) => d && ["facebook", "instagram", "reel"].every((f) => d.downloads?.[f]);
  const startReviewCorrection = () => {
    correctionKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    setReviewDraft({ business: { ...(detail.business || {}) }, content: { ...(detail.content || {}) }, objective_type: detail.objective_type || "" });
    setEditingReview(true);
  };
  const setReviewField = (section, key, value) => setReviewDraft((old) => ({
    ...old, [section]: { ...(old[section] || {}), [key]: value },
  }));
  const submitReviewCorrection = async () => {
    if (!window.confirm("Salvare i dati corretti e avviare una nuova generazione canonica? La correzione non tocca pagamenti o revisioni del cliente, ma può usare nuove chiamate AI entro il tetto previsto.")) return;
    setReviewSaving(true);
    try {
      await api.post(`/admin/flyer/projects/${detail.id}/review-correction`, {
        ...reviewDraft, idempotency_key: correctionKeyRef.current,
      }, { headers: { "Idempotency-Key": correctionKeyRef.current } });
      setEditingReview(false);
      await open(detail.id); load();
    } catch (e) { alert(e?.response?.data?.detail || "Correzione non disponibile."); }
    finally { setReviewSaving(false); }
  };
  const approveHumanReview = async () => {
    if (!window.confirm("Confermi di avere aperto i tre file e verificato personalmente titolo, descrizione, data, ora, contatti e leggibilità?")) return;
    try {
      await api.post(`/admin/flyer/projects/${detail.id}/approve-review`, { confirm_facts_reviewed: true });
      await open(detail.id); load();
    } catch (e) { alert(e?.response?.data?.detail || "Approvazione non disponibile."); }
  };
  // FASE 5B/5C — comandi workflow revisione umana (endpoint già esistenti)
  const loadF5b = useCallback(async (pid) => {
    try { const { data } = await api.get("/admin/flyer/review/queue");
      setF5b((data.projects || []).find((p) => p.id === pid) || null); }
    catch { setF5b(null); }
  }, []);
  useEffect(() => {
    if (detail && !detail.delivered && (detail.status === "needs_review" || (detail.creative_status || {}).state === "needs_review")) {
      loadF5b(detail.id); setDeliv(null);
    } else { setF5b(null); setDeliv(null); }
  }, [detail, loadF5b]);
  const dlCanva = async (fmt) => {
    setF5bBusy(`canva:${fmt}`);
    try {
      const r = await api.get(`/admin/flyer/projects/${detail.id}/canva-package/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `canva_${fmt}.zip`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Pacchetto Canva non disponibile."); }
    finally { setF5bBusy(""); }
  };
  const upFinal = async (fmt, file) => {
    if (!file) return; setF5bBusy(`up:${fmt}`);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/flyer/projects/${detail.id}/final-upload/${fmt}`, fd);
      await loadF5b(detail.id);
    } catch (e) { alert(e?.response?.data?.detail || "File finale non valido."); }
    finally { setF5bBusy(""); }
  };
  const prevFinal = async (fmt) => {
    try {
      const r = await api.get(`/admin/flyer/projects/${detail.id}/final/${fmt}/preview`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { alert("Anteprima non disponibile."); }
  };
  const approveFinal = async () => {
    setF5bBusy("approve");
    try {
      const { data } = await api.post(`/admin/flyer/projects/${detail.id}/approve-final`);
      setDeliv(data); await open(detail.id); load(); await loadF5b(detail.id);
    } catch (e) { alert(e?.response?.data?.detail || "Approvazione non disponibile."); }
    finally { setF5bBusy(""); }
  };
  const resendFinal = async () => {
    setF5bBusy("resend");
    try {
      const { data } = await api.post(`/admin/flyer/projects/${detail.id}/resend-final-delivery`);
      setDeliv({ status: data.ok ? "delivered" : "approved_pending_delivery", email_sent: !!data.ok });
      await open(detail.id); load();
    } catch (e) { alert(e?.response?.data?.detail || "Reinvio non disponibile."); }
    finally { setF5bBusy(""); }
  };

  return (
    <div ref={listRef} data-testid="admin-flyer-office" className="scroll-mt-24">
      <FlyerReviewSettings />
      <div className="flex flex-wrap gap-2 mb-4">
        <span data-testid="flyer-count-total" className="text-sm font-semibold text-brand-navy bg-brand-light px-3 py-1 rounded-full">Totale: {data.total_all}</span>
        {Object.entries(data.counts).map(([k, v]) => (
          <span key={k} className="text-xs font-semibold text-brand-navy/70 bg-white border border-brand-navy/10 px-2.5 py-1 rounded-full">{SL(k)}: {v}</span>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-navy/40" />
          <Input data-testid="flyer-admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice, nome, email o telefono…" className="pl-9 rounded-full" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="flyer-admin-status" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{SL(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <div className="overflow-auto rounded-2xl border border-brand-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-brand-light text-brand-navy/60 text-left"><tr>
            <th className="p-3">Codice</th><th className="p-3">Cliente</th><th className="p-3">Modello</th><th className="p-3">Obiettivo</th><th className="p-3">Stato</th><th className="p-3">Scadenza</th><th className="p-3">Creato</th>
          </tr></thead>
          <tbody className="divide-y divide-brand-navy/5">
            {data.items.map((p) => (
              <tr key={p.id} data-testid={`flyer-row-${p.id}`} onClick={() => open(p.id)} className="hover:bg-brand-light cursor-pointer">
                <td className="p-3 font-mono text-xs">{p.project_code}</td>
                <td className="p-3">{p.name || "—"}<span className="block text-brand-navy/40 text-xs">{p.email}</span></td>
                <td className="p-3">{p.creative_mode === "free" ? <span className="font-semibold text-brand-orange">Lascia fare a noi</span> : p.template_code}</td>
                <td className="p-3 text-brand-navy/60">{p.objective_type}</td>
                <td className="p-3"><span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{SL(p.status)}</span></td>
                <td className="p-3">
                  {p.sla_badge ? (
                    <span data-testid={`flyer-sla-badge-${p.id}`} title={p.sla_badge.remaining || ""}
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.sla_badge.level === "overdue" ? "bg-red-100 text-red-700" : p.sla_badge.level === "soon" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {p.sla_badge.label}
                    </span>
                  ) : <span className="text-brand-navy/30 text-xs">—</span>}
                </td>
                <td className="p-3 text-brand-navy/50">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={7} className="p-6 text-center text-brand-navy/40">Nessun progetto.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} pages={data.pages} total={data.total} onPage={goPage} />

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setEditingReview(false); } }}>
        <DialogContent ref={detailRef} className="max-w-3xl max-h-[88vh] overflow-auto" data-testid="flyer-detail">
          {detail && (<>
            <DialogHeader><DialogTitle>{detail.project_code} · {detail.creative_mode === "free" ? "Lascia fare a noi" : detail.template_code}</DialogTitle></DialogHeader>
            {detail.creative_mode === "free" && (
              <div data-testid="flyer-detail-free-mode" className="rounded-xl border border-brand-orange/30 bg-brand-orange/5 p-3 text-sm">
                <p className="font-semibold text-brand-orange">Lascia fare a noi · libertà creativa</p>
                <p className="mt-1 text-xs text-brand-navy/60">Nessun modello obbligatorio. Il brief resta prioritario e vengono analizzati tutti gli indirizzi online compilati.</p>
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
              {detail.template && ["facebook", "instagram", "reel"].map((f) => (
                <div key={f} className="bg-brand-light rounded-xl aspect-[4/5] flex items-center justify-center p-1 border border-brand-navy/10">
                  <img src={FLY_ASSET + detail.template.previews[f]} alt={f} className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-brand-navy/5 p-3"><p className="text-xs text-brand-navy/50">Cliente</p>{detail.name || "—"} · {detail.email || "—"}</div>
              <div className="rounded-lg bg-brand-navy/5 p-3"><p className="text-xs text-brand-navy/50">Obiettivo / Collezione</p>{detail.objective_type} · {detail.collection}</div>
            </div>
            <div className="mt-3 text-sm"><p className="font-semibold text-brand-navy mb-1">Attività</p>
              <p className="text-brand-navy/70">{Object.entries(detail.business || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</p></div>
            <div className="mt-3 text-sm"><p className="font-semibold text-brand-navy mb-1">Contenuti</p>
              <p className="text-brand-navy/70">{Object.entries(detail.content || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</p></div>
            {detail.assets?.length > 0 && <div className="mt-3"><p className="font-semibold text-brand-navy mb-1 text-sm">Logo e immagini</p>
              <div className="grid grid-cols-4 gap-2">{detail.assets.map((a) => (
                <div key={a.id} className="bg-brand-light rounded-lg aspect-square flex items-center justify-center p-1 border border-brand-navy/10">
                  <img src={FLY_ASSET + a.url} alt={a.kind} className="w-full h-full object-contain" /></div>))}</div></div>}
            {detail.qc && (detail.qc.technical_score != null || detail.qc.issues?.length > 0) && (
              <div data-testid="flyer-detail-qc" className={`mt-3 text-sm rounded-lg border p-3 ${detail.qc.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                <div className="flex flex-wrap gap-3 font-semibold">
                  <span data-testid="qc-technical">Tecnico: {detail.qc.technical_score ?? "-"}/100</span>
                  <span data-testid="qc-visual">Visivo: {detail.qc.visual_score ?? "-"}/100</span>
                  <span data-testid="qc-overall">Complessivo: {detail.qc.overall_score ?? "-"}/100</span>
                  <span data-testid="qc-coherence">Coerenza formati: {detail.qc.coherence != null ? Math.round(detail.qc.coherence * 100) + "%" : "-"}</span>
                  {detail.creative_mode !== "free" && <span data-testid="qc-template">Aderenza modello: {detail.qc.template_adherence != null ? detail.qc.template_adherence + "/100" : "-"}</span>}
                </div>
                {detail.qc.motivazione && <div className="mt-2">{detail.qc.motivazione}</div>}
                {detail.qc.issues?.length > 0 && <div className="mt-1">Segnalazioni QC: {detail.qc.issues.join(", ")}</div>}
              </div>
            )}
            {(() => {
              const r = detail.brand_online_research;
              const SR = { completed: { t: "Completata", c: "bg-emerald-100 text-emerald-700" }, partially_completed: { t: "Parziale", c: "bg-amber-100 text-amber-700" }, not_provided: { t: "Nessun URL fornito", c: "bg-brand-navy/5 text-brand-navy/50" }, unavailable: { t: "Fonti non accessibili", c: "bg-amber-100 text-amber-700" }, failed: { t: "Errore", c: "bg-red-100 text-red-700" } };
              const s = r ? (SR[r.status] || { t: r.status, c: "bg-brand-navy/5 text-brand-navy/50" }) : null;
              return (
                <div data-testid="flyer-detail-brand-research" className="mt-3 text-sm rounded-lg border border-brand-navy/10 bg-white p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-semibold text-brand-navy flex items-center gap-2"><Search className="h-4 w-4 text-brand-electric" /> Analisi online del brand</span>
                    <div className="flex items-center gap-2">
                      {s && <span data-testid="brand-research-status" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.c}`}>{s.t}</span>}
                      <button data-testid="brand-research-btn" onClick={reBrand} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-navy/5 text-brand-navy hover:bg-brand-navy/10">Rianalizza</button>
                    </div>
                  </div>
                  {r && r.status !== "not_provided" ? (
                    <div className="mt-2 space-y-1 text-brand-navy/70 text-xs">
                      {r.brand_summary && <p><span className="font-semibold text-brand-navy">Sintesi:</span> {r.brand_summary}</p>}
                      {r.visual_style && <p><span className="font-semibold text-brand-navy">Stile visivo:</span> {r.visual_style}</p>}
                      {r.recurring_colors && <p><span className="font-semibold text-brand-navy">Colori:</span> {r.recurring_colors}</p>}
                      {r.communication_tone && <p><span className="font-semibold text-brand-navy">Tono:</span> {r.communication_tone}</p>}
                      {r.apparent_audience && <p><span className="font-semibold text-brand-navy">Pubblico:</span> {r.apparent_audience}</p>}
                      {r.conflicts_with_customer_brief && r.conflicts_with_customer_brief.toLowerCase() !== "nessuno" && <p className="text-amber-700"><span className="font-semibold">Conflitti col brief (prevale il brief):</span> {r.conflicts_with_customer_brief}</p>}
                      {r.sources_accessible?.length > 0 && <p className="text-brand-navy/45">Fonti accessibili: {r.sources_accessible.join(", ")}</p>}
                      {r.sources_unavailable?.length > 0 && <p className="text-brand-navy/45">Non accessibili: {r.sources_unavailable.map((u) => (typeof u === "string" ? u : `${u.url} (${u.reason})`)).join(", ")}</p>}
                      <p className="text-brand-navy/40">Modello: {r.model_used} · {r.analyzed_at ? fmtDate(r.analyzed_at) : "-"}</p>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-brand-navy/50">{r ? "Nessun indirizzo online fornito: si procede con brief e logo." : "Verranno analizzati tutti gli indirizzi compilati: sito, Facebook, Instagram e TikTok."}</p>
                  )}
                </div>
              );
            })()}
            {detail.live_preview && !detail.downloads && ["queued", "working", "qc"].includes((detail.creative_status || {}).state) && (
              <div className="mt-3" data-testid="flyer-detail-live-preview">
                <p className="text-xs font-semibold text-brand-navy/60 mb-1.5">Anteprima in tempo reale (bozza in composizione)</p>
                <div className="grid grid-cols-3 gap-2 max-w-md">
                  {["facebook", "instagram", "reel"].map((f) => (
                    <div key={f} className="rounded-lg overflow-hidden border border-brand-navy/10 bg-brand-light aspect-[4/5]">
                      <img src={`${FLY_ASSET}/api/flyer/projects/${detail.id}/preview/${f}`} alt={`Anteprima ${f}`} className="w-full h-full object-cover"
                           data-testid={`monitor-live-preview-${f}`} onError={(e) => { e.currentTarget.style.opacity = "0.15"; }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.downloads && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="flyer-detail-downloads">
                <span className="text-xs text-brand-navy/50 w-full">File generati{detail.delivered ? " (consegnati)" : ""}:</span>
                {["facebook", "instagram", "reel"].map((f) => (
                  <button key={f} data-testid={`flyer-dl-${f}`} onClick={() => dlAdmin(detail.downloads[f])} className="text-xs text-brand-navy/70 border border-brand-navy/15 rounded-full px-3 py-1">{FLY_FMT[f]}</button>
                ))}
                <button data-testid="flyer-dl-zip" onClick={() => dlAdmin(detail.downloads.zip)} className="text-xs text-white bg-brand-electric rounded-full px-3 py-1">ZIP</button>
              </div>
            )}
            {inHumanReview(detail) && (
              <div data-testid="flyer-human-review-panel" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-semibold">Revisione umana richiesta</p>
                <p className="mt-1 text-xs">Tutti e tre i formati (Facebook, Instagram, Reel/Story) richiedono revisione umana obbligatoria: i punteggi QC sono solo informativi e nessun automatismo consegna prima di "Approva e invia". Scarica i pacchetti Canva, completa la revisione e carica i file definitivi.</p>
                {detail.review_notification && (
                  <p data-testid="flyer-review-notification" className="mt-2 text-xs">
                    Notifiche — cliente: {detail.review_notification.customer_status || detail.review_notification.status || "in preparazione"}
                    {" · "}amministratore: {detail.review_notification.admin_status || detail.review_notification.status || "in preparazione"}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button data-testid="flyer-edit-review-btn" onClick={startReviewCorrection} variant="outline" className="rounded-full text-xs">Correggi i dati</Button>
                  {hasReviewOutputs(detail) && (
                    <Button data-testid="flyer-approve-review-btn" onClick={approveHumanReview} className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white">Approva dopo verifica</Button>
                  )}
                </div>
                {f5b && (
                  <div data-testid="flyer-fase5b-panel" className="mt-3 border-t border-amber-200 pt-3 space-y-2">
                    <p className="font-semibold text-xs">Pacchetto Canva e file finali</p>
                    {(f5b.review_due_display || f5b.remaining) && (
                      <div data-testid="f5b-sla" className="rounded-lg bg-white/70 border border-amber-200 p-2 text-[11px] flex flex-wrap gap-x-4 gap-y-1">
                        <span>Consegna prevista entro: <b>{f5b.review_due_display || "—"}</b></span>
                        {f5b.remaining && <span>Tempo lavorativo rimanente: <b className={f5b.remaining.overdue ? "text-red-600" : "text-emerald-700"}>{f5b.remaining.remaining_label}</b></span>}
                      </div>
                    )}
                    {f5b.qc && (f5b.qc.overall_score != null) && (
                      <div data-testid="f5b-qc-info" className="rounded-lg bg-white/70 border border-amber-200 p-2 text-[11px]">
                        <span className="font-semibold">QC (solo informativo): </span>
                        Tecnico {f5b.qc.technical_score ?? "-"}/100 · Visivo {f5b.qc.visual_score ?? "-"}/100 · Complessivo {f5b.qc.overall_score ?? "-"}/100
                        <span className="block text-brand-navy/50">Nessun punteggio autorizza la consegna: serve la revisione umana.</span>
                      </div>
                    )}
                    {f5b.logo?.has_logo && (
                      <div data-testid="f5b-logo" className="rounded-lg bg-white/70 border border-amber-200 p-2 text-[11px]">
                        <p className="font-semibold mb-1">Logo fornito dal cliente</p>
                        {["original", "modified"].map((slot) => {
                          const lg = f5b.logo[slot];
                          if (!lg) return null;
                          return (
                            <div key={slot} className="flex items-center justify-between gap-2 py-0.5">
                              <span className="truncate">{slot === "original" ? "Originale" : "Modificato (admin)"}: {lg.filename || "logo"} · {lg.content_type} {lg.pixels ? `· ${lg.pixels[0]}×${lg.pixels[1]}px` : ""} {lg.size ? `· ${Math.round(lg.size/1024)}KB` : ""}</span>
                              <a data-testid={`f5b-logo-dl-${slot}`} href={`${FLY_ASSET}${lg.download_url}`} target="_blank" rel="noreferrer" className="text-brand-electric font-semibold hover:underline whitespace-nowrap">{slot === "original" ? "Scarica logo originale" : "Scarica logo modificato"}</a>
                            </div>
                          );
                        })}
                        <label data-testid="f5b-logo-upload" className="cursor-pointer text-brand-navy/70 hover:underline inline-block mt-1">
                          Carica versione modificata
                          <input type="file" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append("file", file); try { await api.post(`/admin/flyer/projects/${detail.id}/logo-upload`, fd); await loadF5b(detail.id); toast.success("Logo modificato caricato"); } catch (er) { toast.error(er?.response?.data?.detail || "Upload non riuscito"); } }} />
                        </label>
                      </div>
                    )}
                    {(f5b.formats || []).map((f) => (
                      <div key={f.format} data-testid={`f5b-format-${f.format}`} className="rounded-lg bg-white/70 border border-amber-200 p-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{FLY_FMT[f.format] || f.format}</span>
                          <span className={f.final_uploaded ? "text-emerald-700" : "text-amber-700"}>{f.final_uploaded ? `Finale v${f.final_version}` : "In attesa file finale"}</span>
                        </div>
                        {Array.isArray(f.qc_initial?.critical_issues) && f.qc_initial.critical_issues.length > 0 && (
                          <p className="mt-1 text-[11px] text-amber-800">Problemi QC: {f.qc_initial.critical_issues.join(", ")}</p>
                        )}
                        {f5b.canva_errors?.[f.format] && (
                          <p data-testid={`f5b-canva-error-${f.format}`} className="mt-1 text-[11px] text-red-600">Pacchetto Canva: {f5b.canva_errors[f.format].message}</p>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <Button data-testid={`f5b-canva-${f.format}`} onClick={() => dlCanva(f.format)} disabled={f5bBusy === `canva:${f.format}`} variant="outline" className="rounded-full text-[11px] h-7">Scarica pacchetto Canva</Button>
                          <label data-testid={`f5b-upload-${f.format}`} className="cursor-pointer text-[11px] rounded-full h-7 px-3 inline-flex items-center border border-brand-navy/20 hover:bg-brand-navy/5">
                            {f5bBusy === `up:${f.format}` ? "Carico…" : "Carica file finale"}
                            <input type="file" className="hidden" onChange={(e) => upFinal(f.format, e.target.files?.[0])} />
                          </label>
                          {f.final_uploaded && <Button data-testid={`f5b-preview-${f.format}`} onClick={() => prevFinal(f.format)} variant="ghost" className="rounded-full text-[11px] h-7">Anteprima</Button>}
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <Button data-testid="f5b-approve-final-btn" onClick={approveFinal} disabled={!f5b.can_approve || f5bBusy === "approve"} className="rounded-full text-xs bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">Approva e consegna</Button>
                      {!f5b.can_approve && (f5b.missing_formats || []).length > 0 && <span className="text-[11px] text-amber-700">Mancano: {f5b.missing_formats.map((m) => FLY_FMT[m] || m).join(", ")}</span>}
                    </div>
                    {(() => {
                      const ds = deliv || (detail.final_delivery_status ? { status: detail.final_delivery_status === "sent" ? "delivered" : "approved_pending_delivery", email_sent: detail.final_delivery_status === "sent", error: detail.final_delivery_error } : null);
                      if (!ds) return null;
                      return (
                        <div data-testid="f5b-delivery-status" className="text-[11px] mt-1">
                          Consegna: <b>{ds.email_sent ? "email inviata" : (ds.status === "delivered" ? "consegnato" : "in attesa / invio non riuscito")}</b>
                          {ds.email_sent === false && (
                            <>
                              <span className="text-red-600"> — invio email non riuscito{ds.error ? ` (${ds.error})` : ""}</span>
                              <Button data-testid="f5b-resend-btn" onClick={resendFinal} disabled={f5bBusy === "resend"} variant="outline" className="ml-2 rounded-full text-[11px] h-7">Reinvia consegna</Button>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            {editingReview && inHumanReview(detail) && (
              <div data-testid="flyer-review-correction-form" className="mt-3 rounded-xl border border-brand-electric/25 bg-brand-light p-4">
                <div className="flex items-start justify-between gap-3">
                  <div><p className="font-semibold text-brand-navy">Correggi il brief</p><p className="text-xs text-brand-navy/55">I campi salvati diventano la nuova fonte esatta dei tre flyer.</p></div>
                  <button type="button" onClick={() => setEditingReview(false)} className="text-brand-navy/50"><X className="h-4 w-4" /></button>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {[
                    ["business_name", "Nome attività"], ["sector", "Settore"], ["city", "Città"],
                    ["address", "Indirizzo"], ["phone", "Telefono"], ["website", "Sito web"],
                    ["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"],
                  ].map(([key, label]) => (
                    <label key={key} className="text-xs text-brand-navy/65">{label}
                      <Input data-testid={`review-business-${key}`} value={reviewDraft.business?.[key] ?? ""} onChange={(e) => setReviewField("business", key, e.target.value)} className="mt-1 bg-white" />
                      {["website", "facebook", "instagram", "tiktok"].includes(key) && (
                        <select data-testid={`review-${key}-usage`} value={reviewDraft.business?.[`${key}_usage`] || "research_only"} onChange={(e) => setReviewField("business", `${key}_usage`, e.target.value)} className="mt-1 w-full rounded-md border border-input bg-white px-2 py-2 text-xs">
                          <option value="research_only">Solo analisi</option>
                          <option value="print">Mostra nel flyer</option>
                        </select>
                      )}
                    </label>
                  ))}
                  {[
                    ["headline", "Titolo"], ["description", "Descrizione"], ["date", "Data"],
                    ["time", "Ora"], ["price", "Prezzo"], ["discount", "Sconto"],
                    ["call_to_action", "Invito all'azione"], ["extra", "Testo extra"],
                  ].map(([key, label]) => (
                    <label key={key} className={`text-xs text-brand-navy/65 ${key === "description" || key === "extra" ? "sm:col-span-2" : ""}`}>{label}
                      {key === "description" || key === "extra" ? (
                        <textarea data-testid={`review-content-${key}`} value={reviewDraft.content?.[key] ?? ""} onChange={(e) => setReviewField("content", key, e.target.value)} rows={3} className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
                      ) : (
                        <Input data-testid={`review-content-${key}`} value={reviewDraft.content?.[key] ?? ""} onChange={(e) => setReviewField("content", key, e.target.value)} className="mt-1 bg-white" />
                      )}
                    </label>
                  ))}
                </div>
                <div className="mt-3 max-w-sm text-xs text-brand-navy/65">Obiettivo
                  <Select value={reviewDraft.objective_type || "altro"} onValueChange={(v) => setReviewDraft((old) => ({ ...old, objective_type: v }))}>
                    <SelectTrigger data-testid="review-objective-type" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["evento", "serata_dj", "promozione", "sconto", "nuovo_prodotto", "apertura", "menu", "altro"].map((v) => <SelectItem key={v} value={v}>{v.replaceAll("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button data-testid="flyer-save-review-correction" onClick={submitReviewCorrection} disabled={reviewSaving} className="rounded-full text-xs bg-brand-electric text-white">
                    {reviewSaving ? "Salvataggio…" : "Salva e rigenera"}
                  </Button>
                  <Button onClick={() => setEditingReview(false)} disabled={reviewSaving} variant="outline" className="rounded-full text-xs">Annulla</Button>
                  <span className="text-xs text-brand-navy/45">Correzioni amministrative usate: {detail.admin_review_corrections || 0}/2</span>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span data-testid="flyer-detail-paid" className={`text-xs font-semibold px-2.5 py-1 rounded-full ${detail.paid ? "bg-emerald-100 text-emerald-700" : "bg-brand-navy/5 text-brand-navy/50"}`}>{detail.paid ? "Pagato" : "Non pagato"}</span>
              {!detail.generated_at && !inHumanReview(detail) && <Button data-testid="flyer-generate-btn" onClick={genNow} variant="outline" className="rounded-full text-xs">Genera ora</Button>}
              {isStuck(detail) && (
                <Button data-testid="flyer-recover-btn" onClick={recoverGen} className="rounded-full text-xs bg-brand-orange text-white">Riprendi generazione</Button>
              )}
              {canRetry(detail) && (
                <Button data-testid="flyer-retry-btn" onClick={recoverGen} className="rounded-full text-xs bg-brand-orange text-white">Riprendi lo stesso progetto</Button>
              )}
              {techLabel(detail) && (
                <span data-testid="flyer-tech-status" className={`text-xs font-semibold ${techLabel(detail).cls}`}>{techLabel(detail).txt}</span>
              )}
              {!inHumanReview(detail) && <Button data-testid="flyer-deliver-btn" onClick={deliverNow} disabled={!detail.paid || !detail.qc?.ok || detail.delivered} className="rounded-full text-xs bg-brand-electric text-white disabled:opacity-40">{detail.delivered ? "Consegnato" : "Consegna"}</Button>}
              <Select value={detail.status} onValueChange={async (v) => { await api.patch(`/admin/flyer/projects/${detail.id}/status`, { status: v }); await open(detail.id); load(); }}>
                <SelectTrigger data-testid="flyer-detail-status" className="w-52 rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{SL(s)}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-xs text-brand-navy/40">Creato {fmtDate(detail.created_at)} · agg. {fmtDate(detail.updated_at)}</span>
            </div>
            {detail.audit?.length > 0 && <div className="mt-3 text-xs text-brand-navy/50"><p className="font-semibold mb-1">Storico modifiche</p>
              {detail.audit.map((a, i) => <p key={i}>· {a.field}: {a.from} → {a.to} ({fmtDate(a.at)})</p>)}</div>}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};
